import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { requestEmailChange, verifyEmailChange } from '../api/client';
import { ArrowLeft, Loader2 } from 'lucide-react';

export function ChangeEmailPage() {
  useDocumentTitle('Change Email');
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newEmail || !newEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      await requestEmailChange({ newEmail });
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    try {
      setIsSubmitting(true);
      await verifyEmailChange({ code });
      await refreshUser();
      setSuccess(true);
      setTimeout(() => navigate('/security'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button onClick={() => navigate('/security')} style={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back to Security</span>
          </button>

          <h1 style={styles.title}>Change Email</h1>

          {success ? (
            <div style={styles.successCard}>
              <p style={styles.successText}>Email updated successfully!</p>
              <p style={styles.redirectText}>Redirecting...</p>
            </div>
          ) : step === 'email' ? (
            <form onSubmit={handleSendCode} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Current Email</label>
                <p style={styles.currentValue}>{user?.email || 'No email set'}</p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>New Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  style={styles.input}
                  required
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <button
                type="submit"
                style={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Sending Code...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} style={styles.form}>
              <p style={styles.hint}>
                We sent a 6-digit code to <strong>{newEmail}</strong>. Enter it below to confirm the change.
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>Verification Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{ ...styles.input, textAlign: 'center' as const, letterSpacing: '8px', fontSize: '24px' }}
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <button
                type="submit"
                style={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Verifying...
                  </>
                ) : (
                  'Verify & Update Email'
                )}
              </button>

              <button
                type="button"
                style={styles.linkButton}
                onClick={() => { setStep('email'); setCode(''); setError(null); }}
              >
                Use a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '24px',
  },
  form: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  currentValue: {
    fontSize: '15px',
    color: colors.gray[600],
    padding: '12px 14px',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '16px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  hint: {
    fontSize: '14px',
    color: colors.gray[600],
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  error: {
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#FEE2E2',
    borderRadius: '8px',
  },
  successCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '32px 24px',
    textAlign: 'center' as const,
  },
  successText: {
    color: '#16A34A',
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '8px',
  },
  redirectText: {
    color: colors.gray[600],
    fontSize: '14px',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  linkButton: {
    display: 'block',
    width: '100%',
    padding: '12px',
    marginTop: '12px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.brand.blue,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center' as const,
  },
};
